<script>
import baseMixin from '@/mixins/baseMixin'
import CommentModel from '@/models/comments/commentModel'
import timesheetMixin from '@/mixins/timesheetMixin'
import { ResolveOperations } from '@/constants/disputes/ResolveOperations'
import { mapGetters } from 'vuex'
import DateFormatToken from '@/constants/date/DateFormatToken'
import { TimesheetOperationTypeInts } from '@/constants/disputes/TimesheetOperationType'
import DisputeTimesheetDetailsClient from '@/models/disputes/disputeTimesheetDetailsClient'
import CommentBase from './comment-base.vue'
import CommentBaseText from './comment-base-text.vue'
import CommentDisputeHistoryActionBtn from './comment-dispute-history-action-btn.vue'

export default {
  name: 'CommentDisputeHistoryClient',
  components: {
    CommentBase,
    CommentBaseText,
    CommentDisputeHistoryActionBtn,
  },
  mixins: [baseMixin, timesheetMixin],
  props: {
    comment: {
      type: CommentModel,
      required: true,
    },
  },
  computed: {
    ...mapGetters({
      resolveStatus: 'disputes/resolveStatus',
    }),
    serialisedComment() {
      return new DisputeTimesheetDetailsClient(
        JSON.parse(this.comment.commentText)
      )
    },
    isSelected() {
      return this.resolveStatus === ResolveOperations.CLIENT
    },
    compButtonText() {
      if (this.isSelected) return this.$t('general.selected')
      return this.$t('comment.disputeHistory.useThisBtnText')
    },
    compCommentTitle() {
      const clientContactStr = this.$t(
        'comment.disputeHistory.client.title.clientName',
        [this.serialisedComment.details.contactName]
      )

      switch (this.serialisedComment.details.operationType) {
        case TimesheetOperationTypeInts.ACCEPT:
          return this.$t('comment.disputeHistory.client.title.noChanges', [
            clientContactStr,
          ])
        case TimesheetOperationTypeInts.MODIFY:
          return this.$t('comment.disputeHistory.client.title.changes', [
            clientContactStr,
          ])
        case TimesheetOperationTypeInts.CANCEL:
          return this.$t('comment.disputeHistory.client.title.cancel', [
            clientContactStr,
          ])
        default:
          return this.$t('comment.disputeHistory.client.title.default', [
            clientContactStr,
          ])
      }
    },
    hasModifedTimesheet() {
      return (
        this.serialisedComment.details.operationType ===
        TimesheetOperationTypeInts.MODIFY
      )
    },
    compStartTimeChange() {
      if (
        this.serialisedComment.details.previousStartTime ===
        this.serialisedComment.details.startTime
      )
        return null

      return `${this.renderLocalTime(
        this.serialisedComment.details.previousStartTime,
        DateFormatToken.HHmmss
      )} ➜ ${this.renderLocalTime(
        this.serialisedComment.details.startTime,
        DateFormatToken.HHmmss
      )}`
    },
    compEndTimeChange() {
      if (
        this.serialisedComment.details.previousEndTime ===
        this.serialisedComment.details.endTime
      )
        return null

      return this.$t('comment.disputeHistory.comparisonText', [
        this.renderLocalTime(
          this.serialisedComment.details.previousEndTime,
          DateFormatToken.HHmmss
        ),
        this.renderLocalTime(
          this.serialisedComment.details.endTime,
          DateFormatToken.HHmmss
        ),
      ])
    },
    compBreakMinutesChange() {
      if (
        (this.serialisedComment.details.previousUnpaidBreakMinutes || 0) ===
        (this.serialisedComment.details.unpaidBreakMinutes || 0)
      )
        return null

      return this.$t('comment.disputeHistory.comparisonText', [
        this.serialisedComment.details.previousUnpaidBreakMinutes || 0,
        `${this.serialisedComment.details.unpaidBreakMinutes || 0} mins`,
      ])
    },
  },
  methods: {
    updateResolveStatus() {
      this.$store.commit('disputes/SET_FLYOUT', {
        key: 'resolveStatus',
        value: ResolveOperations.CLIENT,
      })
    },
  },
}
</script>

<template>
  <CommentBase
    :added-by="comment.addedBy"
    :subtitle="comment.getDateCreatedTimestamp()"
    :active="isSelected"
  >
    <div class="d-flex">
      <div class="flex-grow-1">
        <CommentBaseText>{{ compCommentTitle }}</CommentBaseText>

        <div v-if="hasModifedTimesheet" class="my-2">
          <CommentBaseText v-if="compStartTimeChange"
            ><span class="font-weight-bold"
              >{{ $t('comment.disputeHistory.startTimeLabel') }}:</span
            >
            {{ compStartTimeChange }}</CommentBaseText
          >
          <CommentBaseText v-if="compEndTimeChange"
            ><span class="font-weight-bold"
              >{{ $t('comment.disputeHistory.endTimeLabel') }}:</span
            >
            {{ compEndTimeChange }}</CommentBaseText
          >
          <CommentBaseText v-if="compBreakMinutesChange"
            ><span class="font-weight-bold"
              >{{ $t('comment.disputeHistory.breakMinsLabel') }}:</span
            >
            {{ compBreakMinutesChange }}</CommentBaseText
          >
        </div>
      </div>

      <div
        class="dispute-history-comment-actions d-flex flex-column justify-space-between align-end ml-4"
      >
        <v-chip
          v-if="comment.isPriorityTimesheet"
          color="primary darken-2"
          small
        >
          <v-icon left small>mdi-clock-outline</v-icon>
          {{ $t('dispute.history.priorityTimesheetBadge') }}
        </v-chip>
        <div v-else></div>

        <CommentDisputeHistoryActionBtn
          :selected="isSelected"
          :text="compButtonText"
          @click="updateResolveStatus"
        />
        <div></div>
      </div>
    </div>
  </CommentBase>
</template>
